// Currency + Country context.
//
// Single source of truth for the customer's selected country/currency.
// Persists to localStorage (`elite_country`). Exposes:
//   country               : full record from COUNTRIES
//   setCountry(cc)        : update selection
//   formatPrice(amount, opts) : Intl.NumberFormat-aware formatter
//   recent                : last 5 country_codes (newest first)
//
// Future-ready: when an FX-rate API lands, plug `convert()` here so every consumer
// gets reactive multi-currency pricing without further plumbing.
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { COUNTRIES, findByCountry } from '../lib/countries';

const STORAGE_KEY = 'elite_country';
const RECENT_KEY = 'elite_country_recent';
const FX_CACHE_KEY = 'elite_fx_cache_v1';
const FX_TTL_MS = 12 * 60 * 60 * 1000;  // 12 hours
const CurrencyContext = createContext(null);

const apiBase = process.env.REACT_APP_BACKEND_URL || '';

export const CurrencyProvider = ({ children }) => {
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem(STORAGE_KEY) || 'IN');
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
  });
  const [fx, setFx] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem(FX_CACHE_KEY) || 'null');
      if (c && Date.now() - c.cached_at < FX_TTL_MS) return c;
    } catch { /* */ }
    return null;
  });

  useEffect(() => { localStorage.setItem(STORAGE_KEY, countryCode); }, [countryCode]);
  useEffect(() => { localStorage.setItem(RECENT_KEY, JSON.stringify(recent)); }, [recent]);

  // Fetch FX rates on mount (or when cache expired)
  useEffect(() => {
    if (fx && Date.now() - fx.cached_at < FX_TTL_MS) return;
    axios.get(`${apiBase}/api/fx/rates`).then((r) => {
      const next = { ...r.data, cached_at: Date.now() };
      localStorage.setItem(FX_CACHE_KEY, JSON.stringify(next));
      setFx(next);
    }).catch(() => {});
  }, [fx]);

  const setCountry = useCallback((cc) => {
    setCountryCode(cc);
    setRecent((prev) => [cc, ...prev.filter((x) => x !== cc)].slice(0, 5));
  }, []);

  const country = useMemo(() => findByCountry(countryCode), [countryCode]);

  // Convert INR base amount → active currency (live FX) or 1.0 fallback.
  const convertFromINR = useCallback((amount) => {
    if (!fx || !fx.rates) return Number(amount);
    const rate = fx.rates[country.currency_code];
    if (!rate) return Number(amount);
    return Number(amount) * rate;
  }, [fx, country.currency_code]);

  const formatPrice = useCallback((amount, opts = {}) => {
    const n = convertFromINR(amount);
    if (!Number.isFinite(Number(n))) return '—';
    try {
      return new Intl.NumberFormat(country.locale, {
        style: 'currency',
        currency: country.currency_code,
        maximumFractionDigits: opts.fractionDigits ?? (country.currency_code === 'INR' ? 0 : 2),
        ...opts.intl,
      }).format(Number(n));
    } catch {
      return `${country.currency_symbol}${Math.round(Number(n)).toLocaleString()}`;
    }
  }, [country, convertFromINR]);

  return (
    <CurrencyContext.Provider value={{
      country, countryCode, setCountry,
      currency: country.currency_code,
      currencySymbol: country.currency_symbol,
      // With live FX wired, prices are now economically accurate. Kept for
      // existing call-sites that still render an `approx` hint.
      isBaseCurrency: country.currency_code === 'INR',
      fx, fxSource: fx?.source || 'pending',
      recent: recent.map(findByCountry),
      countries: COUNTRIES,
      formatPrice,
      convertFromINR,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used inside CurrencyProvider');
  return ctx;
};
